import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import {
  Zap,
  BarChart3,
  Briefcase,
  Users,
  TrendingUp,
  FileText,
  Star,
  Home
} from "lucide-react";

const JobSeekerAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/analytics/jobseeker`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const profileScore = analytics?.overview?.profile_score || 0;

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-crimson flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">REKRUTOR</span>
            </Link>
            <nav className="hidden md:flex items-center gap-4">
              <Link to="/dashboard/jobseeker" className="text-gray-400 hover:text-white transition-colors">Dashboard</Link>
              <span className="text-crimson font-bold">Analytics</span>
            </nav>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl font-bold mb-2">
              CAREER <span className="text-crimson">ANALYTICS</span>
            </h1>
            <p className="text-gray-400">Track your job search progress and profile performance</p>
          </motion.div>

          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Applications", value: analytics?.overview?.total_applications || 0, icon: Briefcase },
              { label: "Interviews", value: analytics?.overview?.completed_interviews || 0, icon: FileText },
              { label: "Connections", value: analytics?.overview?.connections || 0, icon: Users },
              { label: "Profile Score", value: `${profileScore}%`, icon: Star }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="bg-obsidian-light border border-[#222] p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className="w-5 h-5 text-crimson" />
                </div>
                <div className="font-heading text-3xl font-bold text-crimson">{stat.value}</div>
                <div className="text-xs uppercase tracking-wider text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Profile Completeness */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="font-heading text-xl font-bold mb-6">PROFILE COMPLETENESS</h3>
              
              <div className="relative w-48 h-48 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="#1a1a1a"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    stroke="#FF2A2A"
                    strokeWidth="12"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${(profileScore / 100) * 553} 553`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="font-heading text-4xl font-bold">{profileScore}%</div>
                    <div className="text-xs text-gray-500 uppercase">Complete</div>
                  </div>
                </div>
              </div>

              {profileScore < 100 && (
                <div className="text-center">
                  <p className="text-gray-400 mb-4">Complete your profile to improve visibility</p>
                  <Button
                    onClick={() => navigate("/profile/build")}
                    className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                  >
                    Complete Profile
                  </Button>
                </div>
              )}
            </motion.div>

            {/* Applications by Status */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="font-heading text-xl font-bold mb-6">APPLICATION STATUS</h3>
              <div className="space-y-4">
                {Object.entries(analytics?.applications_by_status || {}).map(([status, count]) => {
                  const total = Object.values(analytics?.applications_by_status || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  const colors = {
                    pending: "bg-yellow-500",
                    reviewed: "bg-blue-500",
                    shortlisted: "bg-green-500",
                    rejected: "bg-red-500"
                  };
                  return (
                    <div key={status}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm capitalize">{status}</span>
                        <span className="text-sm text-gray-400">{count}</span>
                      </div>
                      <div className="h-2 bg-[#1a1a1a] overflow-hidden">
                        <div 
                          className={`h-full ${colors[status] || "bg-crimson"}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics?.applications_by_status || {}).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No applications yet</p>
                )}
              </div>
            </motion.div>

            {/* Application History */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6 lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="font-heading text-xl font-bold mb-6">RECENT APPLICATIONS</h3>
              <div className="space-y-3">
                {analytics?.application_history?.map((app, index) => (
                  <div 
                    key={app.application_id}
                    className="flex items-center justify-between p-4 bg-obsidian border border-[#1a1a1a]"
                  >
                    <div>
                      <h4 className="font-bold">{app.job_title}</h4>
                      <p className="text-sm text-gray-500">{app.company}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 text-xs uppercase ${
                        app.status === "shortlisted" ? "bg-green-500/20 text-green-400" :
                        app.status === "rejected" ? "bg-red-500/20 text-red-400" :
                        "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        {app.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(app.applied_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                {(!analytics?.application_history || analytics.application_history.length === 0) && (
                  <p className="text-gray-500 text-center py-4">No application history</p>
                )}
              </div>
            </motion.div>

            {/* Skills */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6 lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="font-heading text-xl font-bold mb-6">YOUR SKILLS</h3>
              <div className="flex flex-wrap gap-2">
                {analytics?.skills?.map((skill, index) => (
                  <span 
                    key={index}
                    className="px-3 py-2 bg-crimson/10 border border-crimson/30 text-crimson"
                  >
                    {skill}
                  </span>
                ))}
                {(!analytics?.skills || analytics.skills.length === 0) && (
                  <p className="text-gray-500">No skills added yet. Update your profile to add skills.</p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerAnalytics;
